import { Button, InlineNotification, Modal, SkeletonText, Stack, Tag, Toggle } from '@carbon/react';
import { asProblem, hasPermission, problemMessage, useAdmin, type RecordValues } from '@pos/admin-ui';
import { useTranslation } from '@pos/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { hoApi } from '../api';
import './signIn.scss';

/**
 * A staff member's HO sign-in (ADR 0121): two-step sign-in and recovery codes left, whether they are linked to the company's
 * provider, and break-glass. Staff managers reset a lost authenticator app or unlink the provider for someone they outrank; only a
 * company-wide admin marks break-glass accounts.
 */
export function StaffSignInTab({ staff }: { staff: RecordValues }) {
  const { t } = useTranslation();
  const { session } = useAdmin();
  const canManage = hasPermission(session, 'ho.org.staff.manage');
  const id = String(staff.id);
  const queryClient = useQueryClient();
  const key = ['ho', 'staff-sign-in', id];
  const status = useQuery({
    queryKey: key,
    queryFn: async () => {
      const { data, error } = await hoApi.GET('/api/v1/identity/staff/{id}/sign-in', { params: { path: { id } } });
      if (!data) throw error;
      return data;
    },
  });
  const [confirming, setConfirming] = useState<'reset' | 'unlink'>();
  const done = () => {
    setConfirming(undefined);
    void queryClient.invalidateQueries({ queryKey: key });
  };
  const breakGlass = useMutation({
    mutationFn: async (on: boolean) => {
      const { data, error } = await hoApi.PUT('/api/v1/identity/staff/{id}/sign-in', {
        params: { path: { id } },
        body: { breakGlass: on },
      });
      if (!data) throw error;
      return data;
    },
    onSuccess: (next) => queryClient.setQueryData(key, next),
  });
  const act = useMutation({
    mutationFn: async (what: 'reset' | 'unlink') => {
      const { error, response } =
        what === 'reset'
          ? await hoApi.POST('/api/v1/identity/staff/{id}/sign-in/mfa/reset', { params: { path: { id } } })
          : await hoApi.POST('/api/v1/identity/staff/{id}/sign-in/sso/unlink', { params: { path: { id } } });
      if (!response.ok) throw error;
    },
    onSuccess: done,
  });

  if (status.isLoading) return <SkeletonText paragraph lineCount={3} aria-label={t('common.loading')} />;
  if (!status.data)
    return (
      <InlineNotification
        kind="error"
        role="alert"
        lowContrast
        hideCloseButton
        title={problemMessage(t, asProblem(status.error))}
      />
    );
  const error = breakGlass.error ?? act.error;

  return (
    <Stack gap={6}>
      <div className="pos-sign-in__tags">
        <Tag type={status.data.enabled ? 'green' : 'gray'}>
          {status.data.enabled ? t('signIn.twoStep.on') : t('signIn.twoStep.off')}
        </Tag>
        <Tag type={status.data.ssoLinked ? 'blue' : 'gray'}>
          {status.data.ssoLinked ? t('signIn.staff.linked') : t('signIn.staff.notLinked')}
        </Tag>
        {status.data.enabled && (
          <span className="pos-sign-in__meta">
            {t('signIn.twoStep.recoveryLeft', { count: status.data.recoveryCodesLeft })}
          </span>
        )}
      </div>
      <Toggle
        id={`break-glass-${id}`}
        labelText={t('signIn.staff.breakGlass')}
        labelA={t('admin.no')}
        labelB={t('admin.yes')}
        toggled={status.data.breakGlass}
        disabled={!canManage || breakGlass.isPending}
        onToggle={(on) => breakGlass.mutate(on)}
      />
      <p className="pos-sign-in__meta">{t('signIn.staff.breakGlassHint')}</p>
      {canManage && (
        <div className="pos-sign-in__actions">
          <Button kind="tertiary" disabled={!status.data.enabled} onClick={() => setConfirming('reset')}>
            {t('signIn.staff.reset')}
          </Button>
          <Button kind="tertiary" disabled={!status.data.ssoLinked} onClick={() => setConfirming('unlink')}>
            {t('signIn.staff.unlink')}
          </Button>
        </div>
      )}
      {error && (
        <InlineNotification
          kind="error"
          role="alert"
          lowContrast
          hideCloseButton
          title={problemMessage(t, asProblem(error))}
        />
      )}
      {confirming && (
        <Modal
          open
          danger
          modalHeading={confirming === 'reset' ? t('signIn.staff.resetConfirm') : t('signIn.staff.unlinkConfirm')}
          primaryButtonText={confirming === 'reset' ? t('signIn.staff.reset') : t('signIn.staff.unlink')}
          secondaryButtonText={t('common.cancel')}
          closeButtonLabel={t('carbon.close')}
          onRequestClose={() => {
            act.reset();
            setConfirming(undefined);
          }}
          onRequestSubmit={() => act.mutate(confirming)}
        >
          <p>{confirming === 'reset' ? t('signIn.staff.resetBody') : t('signIn.staff.unlinkBody')}</p>
        </Modal>
      )}
    </Stack>
  );
}
